import type { TimeSeriesPoint } from '../TimeSeriesChart.types';

/**
 * Binary search for the first point in `points[fromIndex..toIndex)` that is past the boundary; `toIndex`
 * when none is. Points must be ascending by timestamp.
 */
function findFirstPointIndex(
  points: readonly TimeSeriesPoint[],
  isPastBoundary: (point: TimeSeriesPoint) => boolean,
  fromIndex = 0,
  toIndex = points.length
): number {
  let left = fromIndex;
  let right = toIndex;

  while (left < right) {
    const middle = Math.floor((left + right) / 2);

    if (isPastBoundary(points[middle])) {
      right = middle;
    } else {
      left = middle + 1;
    }
  }

  return left;
}

export function findPointAtOrAfterIndex(
  points: readonly TimeSeriesPoint[],
  timestamp: number,
  fromIndex?: number,
  toIndex?: number
): number {
  return findFirstPointIndex(points, (point) => point.timestamp >= timestamp, fromIndex, toIndex);
}

export function findPointAfterIndex(points: TimeSeriesPoint[], timestamp: number): number {
  return findFirstPointIndex(points, (point) => point.timestamp > timestamp);
}

export function findNearestTimeSeriesPointIndex(points: TimeSeriesPoint[], timestamp: number): number | undefined {
  if (points.length === 0) {
    return undefined;
  }

  const afterIndex = findPointAtOrAfterIndex(points, timestamp);

  if (afterIndex === 0) {
    return 0;
  }

  if (afterIndex >= points.length) {
    return points.length - 1;
  }

  const beforeIndex = afterIndex - 1;
  const distanceToBefore = timestamp - points[beforeIndex].timestamp;
  const distanceToAfter = points[afterIndex].timestamp - timestamp;

  return distanceToBefore <= distanceToAfter ? beforeIndex : afterIndex;
}

/**
 * Among the points within `[fromTimestamp, toTimestamp]`, the one whose value is closest to `value`;
 * a tie goes to the earlier point. Undefined when no point falls in the range.
 *
 * Used where one pixel column covers many samples: a zoomed-out spike is drawn from samples a few
 * minutes apart, and the point nearest in time to the pointer is usually the baseline next to the
 * peak rather than the peak the user is pointing at.
 */
export function findNearestPointByValueInRange(
  points: TimeSeriesPoint[],
  fromTimestamp: number,
  toTimestamp: number,
  value: number
): number | undefined {
  const endIndex = findPointAfterIndex(points, toTimestamp);
  let nearestIndex: number | undefined;
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (let index = findPointAtOrAfterIndex(points, fromTimestamp); index < endIndex; index += 1) {
    const distance = Math.abs(points[index].value - value);

    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = index;
    }
  }

  return nearestIndex;
}
